import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { NgSelectComponent } from '@ng-select/ng-select';
import { Observable, of, Subject } from 'rxjs';
import { debounceTime, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AutocompleteItem } from './autocomplete-item';
import { AutocompleteItemsService } from './autocomplete-items.service';

@Component({
  selector: 'bom-autocomplete',
  templateUrl: './autocomplete.component.html',
  styleUrls: ['./autocomplete.component.scss'],
})
export class AutocompleteComponent implements OnInit, OnDestroy {
  @Input()
  itemsService: AutocompleteItemsService;

  @Input()
  placeholder: string;

  @Input()
  loadOnFocus: boolean;

  @Input()
  closeDropdownOnEmptyInput = true;

  @Input()
  showButton = false;

  @Input()
  disableSearch$: Observable<boolean> = of(false);

  @Input()
  timeoutWarningDelay = 5000;

  @Output()
  itemSelected = new EventEmitter<AutocompleteItem>();

  @Output()
  valueSelected = new EventEmitter<any>();

  @Output()
  valueActivated = new EventEmitter<void>();

  @Output()
  timeoutWarning = new EventEmitter<void>();

  @Output()
  itemsLoaded = new EventEmitter<void>();

  @ViewChild('select', { static: true })
  select: NgSelectComponent;

  items: AutocompleteItem[];
  typeahead$ = new Subject<string>();
  searchLoading = false;
  selectedItem: AutocompleteItem;
  dropdownOpen = false;
  notFoundText: string;

  private suppressClickEvent = false;
  private suppressDropdownOpen = false;
  private componentDestroyed$ = new Subject();
  private timeoutTimer: NodeJS.Timer;

  constructor() {}

  ngOnInit(): void {
    this.typeahead$
      .pipe(
        tap(() => this.startTimeoutTimer()),
        tap(() => (this.notFoundText = null)),
        debounceTime(250),
        map(term => (term === null ? '' : term)),
        map(term => term.trim()),
        tap(() => (this.searchLoading = true)),
        switchMap(term => this.itemsService.getAutocompleteItems(term))
      )
      .subscribe(items => {
        this.searchLoading = false;
        this.notFoundText = 'Ingen resultater';
        this.items = items;
        this.itemsLoaded.emit();
        clearTimeout(this.timeoutTimer);
      });
  }

  onChange(item: AutocompleteItem) {
    this.suppressClickEvent = true;

    if (!item) {
      return;
    }

    this.selectedItem = item;
    this.select.searchTerm = this.selectedItem.label;
    this.dropdownOpen = false;
    this.itemSelected.emit(this.selectedItem);
    this.valueSelected.emit(this.selectedItem.value);
  }

  onSearch(input: string) {
    this.items = undefined;
    if (!input && this.closeDropdownOnEmptyInput) {
      this.dropdownOpen = false;
    } else {
      this.dropdownOpen = true;
    }
    if (input.length === 0) {
      this.select.searchTerm = '';
    }

    if (this.selectedItem) {
      this.selectedItem = null;
      this.itemSelected.emit(this.selectedItem);
      this.valueSelected.emit(null);
      this.valueActivated.emit(null);
    }
  }

  onBlur() {
    this.dropdownOpen = false;
    if (!this.items || this.items.length === 0) {
      this.suppressDropdownOpen = true;
    }
  }

  onEnter() {
    if (!this.dropdownOpen && this.selectedItem) {
      this.valueActivated.emit(this.selectedItem.value);
    }
  }

  onClick() {
    if (this.suppressClickEvent) {
      this.suppressClickEvent = false;
      return;
    }

    if (this.loadOnFocus && !this.dropdownOpen) {
      this.loadItems();
    }
  }

  onFocus() {
    if (this.loadOnFocus) {
      this.suppressClickEvent = true;
      this.loadItems(this.select.searchTerm);
    }
  }

  onButtonClick() {
    this.suppressDropdownOpen = false;
    this.loadItems(this.select.searchTerm);
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }

  clear() {
    this.select.typeahead.next('');
    this.select.searchTerm = '';
    this.select.itemsList.clearSelected();
    this.select.itemsList.resetFilteredItems();
  }

  private loadItems(term: string = '') {
    this.startTimeoutTimer();

    if (this.searchLoading) {
      return;
    }
    if (!term) {
      term = '';
    }
    this.searchLoading = true;
    this.itemsService
      .getAutocompleteItems(term)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(items => {
        this.searchLoading = false;
        this.items = items;
        if (this.suppressDropdownOpen) {
          this.suppressDropdownOpen = false;
        } else {
          this.dropdownOpen = true;
        }
        clearTimeout(this.timeoutTimer);
        this.itemsLoaded.emit();
      });
  }

  private onTimeout() {
    this.timeoutWarning.emit();
  }

  private startTimeoutTimer() {
    if (this.timeoutTimer) {
      clearTimeout(this.timeoutTimer);
    }
    this.timeoutTimer = setTimeout(() => this.onTimeout(), this.timeoutWarningDelay);
  }
}
